import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus, Trash2, ChevronUp, ChevronDown, RefreshCw, Copy, KeyRound, X, Bot, Download,
} from 'lucide-react';
import { CRMUser, ExecInboxMailboxConfig, ExecInboxStage, ExecInboxAgentLogEntry } from '../../types';
import {
  updateMailboxConfig, updateGuardrails, saveStages, rotateAgentApiKey, subscribeAgentLog,
  ensureExecGmailWatch,
} from '../../utils/execInboxApi';
import { connectGmail } from '../../utils/gmailAuth';
import { downloadExecInboxAgentGuide } from '../../utils/execInboxAgentGuide';

interface Props {
  ownerUid: string;
  ownerName: string;
  ownerEmail: string;
  cfg: ExecInboxMailboxConfig;
  ownerConnected: boolean;
  currentUserUid: string;
  currentUserConnected: boolean;
  crmUsers: CRMUser[];
  onToast: (msg: string, error?: boolean) => void;
}

const AGENT_ENDPOINT = 'https://us-central1-lctf-projects.cloudfunctions.net/execInboxAgentApi';

const AUTONOMY_DESCRIPTIONS = [
  'Observe — the agent can read the mailbox but makes no changes.',
  'Classify — the agent can move threads between funnel stages.',
  'Draft — the agent can also propose replies for a human to approve.',
  'Auto-send — the agent can send replies automatically, within guardrails.',
  'Full — the agent can also auto-file / auto-archive without review.',
];

function Switch({ on, onClick }: { on: boolean; onClick: () => void }) {
  return <button type="button" className={`exec-switch${on ? ' on' : ''}`} onClick={onClick} aria-pressed={on} />;
}

const POLICIES: ExecInboxStage['agentPolicy'][] = ['observe', 'classify', 'draft', 'autosend', 'autofile'];

const ExecSuiteSettings: React.FC<Props> = ({
  ownerUid, ownerName, ownerEmail, cfg, ownerConnected,
  currentUserUid, currentUserConnected, crmUsers, onToast,
}) => {
  const isOwner = currentUserUid === ownerUid;
  const [stages, setStages] = useState<ExecInboxStage[]>(cfg.stages || []);
  const [stagesDirty, setStagesDirty] = useState(false);
  const [revealKey, setRevealKey] = useState(false);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [log, setLog] = useState<(ExecInboxAgentLogEntry & { id: string })[]>([]);

  const [allowList, setAllowList] = useState((cfg.guardrails?.senderAllowList || []).join(', '));
  const [denyList, setDenyList] = useState((cfg.guardrails?.senderDenyList || []).join(', '));

  useEffect(() => { setStages(cfg.stages || []); setStagesDirty(false); }, [cfg.stages]);
  useEffect(() => subscribeAgentLog(ownerUid, setLog), [ownerUid]);

  const delegateUsers = useMemo(
    () => crmUsers.filter((u) => (cfg.delegates || []).includes(u.uid)),
    [crmUsers, cfg.delegates],
  );
  const addableUsers = useMemo(
    () => crmUsers.filter((u) => u.uid !== ownerUid && !(cfg.delegates || []).includes(u.uid) && (u.role === 'employee' || u.role === 'admin' || u.role === 'staff')),
    [crmUsers, cfg.delegates, ownerUid],
  );

  const [pushBusy, setPushBusy] = useState(false);

  const set = (partial: Partial<ExecInboxMailboxConfig>) =>
    updateMailboxConfig(ownerUid, partial).catch((e) => onToast(e.message, true));

  const registerPush = async () => {
    setPushBusy(true);
    try {
      const r = await ensureExecGmailWatch(ownerUid);
      if (r.skipped) {
        onToast(r.message || 'Push watch active on desktop agent');
      } else {
        onToast('Gmail push registered — new mail should appear within seconds');
      }
    } catch (e) {
      onToast((e as Error).message, true);
    } finally {
      setPushBusy(false);
    }
  };

  const guard = cfg.guardrails!;

  // ---- Stage editor helpers ----
  const updateStage = (idx: number, patch: Partial<ExecInboxStage>) => {
    setStages((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
    setStagesDirty(true);
  };
  const moveStage = (idx: number, dir: -1 | 1) => {
    setStages((prev) => {
      const arr = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return prev;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return arr.map((s, i) => ({ ...s, order: i }));
    });
    setStagesDirty(true);
  };
  const addStage = () => {
    const id = `stage-${Date.now().toString(36)}`;
    setStages((prev) => [...prev, { id, label: 'New stage', gmailLabelName: 'New stage', order: prev.length, accent: '#64748B', icon: 'Tag', agentPolicy: 'classify' }]);
    setStagesDirty(true);
  };
  const archiveStage = (idx: number) => {
    setStages((prev) => prev.map((s, i) => (i === idx ? { ...s, archived: !s.archived } : s)));
    setStagesDirty(true);
  };
  const commitStages = async () => {
    try { await saveStages(ownerUid, stages.map((s, i) => ({ ...s, order: i }))); setStagesDirty(false); onToast('Funnel stages saved'); }
    catch (e) { onToast((e as Error).message, true); }
  };

  const rotateKey = async () => {
    try { const k = await rotateAgentApiKey(ownerUid); setGeneratedKey(k); setRevealKey(true); onToast('New agent key generated — copy it now'); }
    catch (e) { onToast((e as Error).message, true); }
  };

  const displayKey = generatedKey || (revealKey ? cfg.agentApiKey || '' : (cfg.agentApiKey ? '••••••••••••••••••••' : ''));

  return (
    <div className="exec-settings">
      <div className="exec-settings-inner">

        {/* Connection */}
        <section className="exec-card">
          <h3>Connection</h3>
          <p className="sub">Gmail labels are the source of truth. Sends use native Gmail delegation, so a reply from an assistant still shows “sent by” them.</p>
          <div className="exec-field" style={{ marginBottom: 12 }}>
            <div>
              <div className="exec-field-label">Gmail push (real-time sync)</div>
              <div className="exec-field-help">
                {!cfg.gmailWatch?.expiration
                  ? 'Not registered — use Register push below, or wait for scheduled fallback.'
                  : cfg.gmailWatch.watchOwner === 'agent'
                    ? 'Watch managed by desktop agent — CRM mirrors via shared Pub/Sub topic.'
                    : 'CRM-registered Gmail watch — mirror updates within seconds.'}
                {cfg.gmailWatch?.lastNotificationAt && (
                  <> Last push {cfg.gmailWatch.lastNotificationAt.toLocaleString()}.</>
                )}
                {cfg.gmailWatch?.expiration && (
                  <> Expires {cfg.gmailWatch.expiration.toLocaleString()}.</>
                )}
                {cfg.gmailWatch?.topicName && (
                  <> Topic: {cfg.gmailWatch.topicName}.</>
                )}
              </div>
            </div>
            <div className="exec-field-control">
              {ownerConnected && (
                <button type="button" className="exec-mini-btn" disabled={pushBusy} onClick={() => registerPush()}>
                  <RefreshCw size={12} className={pushBusy ? 'spin' : ''} /> Register push
                </button>
              )}
            </div>
          </div>
          <div className="exec-field">
            <div>
              <div className="exec-field-label">Executive Suite</div>
              <div className="exec-field-help">Enable the funnel for {ownerName}’s mailbox.</div>
            </div>
            <div className="exec-field-control"><Switch on={cfg.enabled} onClick={() => set({ enabled: !cfg.enabled })} /></div>
          </div>
          <div className="exec-field">
            <div>
              <div className="exec-field-label">Mailbox Gmail ({ownerEmail || 'no address'})</div>
              <div className="exec-field-help">{ownerConnected ? 'Connected.' : 'The owner must connect Google to sync this mailbox.'}</div>
            </div>
            <div className="exec-field-control">
              {ownerConnected ? <span className="exec-log-result applied">Connected</span>
                : isOwner ? <button className="exec-ghost-btn" onClick={() => connectGmail(currentUserUid)}>Connect Google</button>
                : <span className="exec-field-help">Owner action</span>}
            </div>
          </div>
          {!isOwner && (
            <div className="exec-field">
              <div>
                <div className="exec-field-label">Your sending account</div>
                <div className="exec-field-help">Connect your own Google account so your sends are attributed to you.</div>
              </div>
              <div className="exec-field-control">
                {currentUserConnected ? <span className="exec-log-result applied">Connected</span>
                  : <button className="exec-ghost-btn" onClick={() => connectGmail(currentUserUid)}>Connect Google</button>}
              </div>
            </div>
          )}
        </section>

        {/* Delegates */}
        <section className="exec-card">
          <h3>Assistants & delegates</h3>
          <p className="sub">People who can operate this mailbox in the suite. Each connects their own Google account for native “sent by” attribution.</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            {delegateUsers.length === 0 && <span className="exec-field-help">No delegates yet.</span>}
            {delegateUsers.map((u) => (
              <span key={u.uid} className="exec-link-chip">
                {u.displayName || u.email}
                <button onClick={() => set({ delegates: (cfg.delegates || []).filter((d) => d !== u.uid) })} aria-label="Remove"><X size={11} /></button>
              </span>
            ))}
          </div>
          <select
            className="exec-mailbox-select"
            value=""
            onChange={(e) => { if (e.target.value) set({ delegates: [...(cfg.delegates || []), e.target.value] }); }}
          >
            <option value="">Add a delegate…</option>
            {addableUsers.map((u) => <option key={u.uid} value={u.uid}>{u.displayName || u.email}</option>)}
          </select>
        </section>

        {/* Agent autonomy */}
        <section className="exec-card">
          <h3>Agent autonomy</h3>
          <p className="sub">Progressive control for the local agent. Effective authority is the lower of this level and each stage’s policy.</p>
          <div className="exec-field">
            <div>
              <div className="exec-field-label">Agent enabled (kill switch)</div>
              <div className="exec-field-help">Master switch for every agent action.</div>
            </div>
            <div className="exec-field-control"><Switch on={cfg.agentEnabled} onClick={() => set({ agentEnabled: !cfg.agentEnabled })} /></div>
          </div>
          <div style={{ marginTop: 10 }}>
            <div className="exec-autonomy">
              {['L0', 'L1', 'L2', 'L3', 'L4'].map((lbl, i) => (
                <button key={lbl} className={`exec-autonomy-step${cfg.autonomyLevel === i ? ' active' : ''}`} onClick={() => set({ autonomyLevel: i as any })}>{lbl}</button>
              ))}
            </div>
            <div className="exec-autonomy-desc">{AUTONOMY_DESCRIPTIONS[cfg.autonomyLevel] || AUTONOMY_DESCRIPTIONS[0]}</div>
          </div>
          <div className="exec-field" style={{ marginTop: 6 }}>
            <div>
              <div className="exec-field-label">Agent sends through</div>
              <div className="exec-field-help">The connected delegate whose Google account the agent uses to send (drives “sent by”).</div>
            </div>
            <div className="exec-field-control">
              <select className="exec-mini-select" value={cfg.agentOperatorUid || ''} onChange={(e) => set({ agentOperatorUid: e.target.value || undefined })}>
                <option value="">— none —</option>
                {[...delegateUsers, ...crmUsers.filter((u) => u.uid === ownerUid)].map((u) => (
                  <option key={u.uid} value={u.uid}>{u.displayName || u.email}</option>
                ))}
              </select>
            </div>
          </div>
        </section>

        {/* Guardrails */}
        <section className="exec-card">
          <h3>Guardrails</h3>
          <p className="sub">Hard limits that apply to every automated send.</p>
          <div className="exec-field">
            <div><div className="exec-field-label">Dry run</div><div className="exec-field-help">Log agent actions without executing them.</div></div>
            <div className="exec-field-control"><Switch on={!!guard.dryRun} onClick={() => updateGuardrails(ownerUid, { dryRun: !guard.dryRun }).catch((e) => onToast(e.message, true))} /></div>
          </div>
          <div className="exec-field">
            <div><div className="exec-field-label">Max auto-sends / day</div><div className="exec-field-help">0 disables auto-send entirely.</div></div>
            <div className="exec-field-control">
              <input type="number" min={0} className="exec-link-input" style={{ width: 80 }} defaultValue={guard.maxAutoSendsPerDay}
                onBlur={(e) => updateGuardrails(ownerUid, { maxAutoSendsPerDay: parseInt(e.target.value, 10) || 0 }).catch((err) => onToast(err.message, true))} />
            </div>
          </div>
          <div className="exec-field">
            <div><div className="exec-field-label">Min confidence</div><div className="exec-field-help">0–1; the agent must meet this to act.</div></div>
            <div className="exec-field-control">
              <input type="number" min={0} max={1} step={0.05} className="exec-link-input" style={{ width: 80 }} defaultValue={guard.minConfidence}
                onBlur={(e) => updateGuardrails(ownerUid, { minConfidence: Math.max(0, Math.min(1, parseFloat(e.target.value) || 0)) }).catch((err) => onToast(err.message, true))} />
            </div>
          </div>
          <div className="exec-field">
            <div><div className="exec-field-label">Quiet hours</div><div className="exec-field-help">No auto-sends in this local window.</div></div>
            <div className="exec-field-control">
              <input type="time" className="exec-mini-select" defaultValue={guard.quietHours?.start || ''}
                onBlur={(e) => updateGuardrails(ownerUid, { quietHours: { start: e.target.value, end: guard.quietHours?.end || '', tz: guard.quietHours?.tz } }).catch((err) => onToast(err.message, true))} />
              <span className="exec-field-help">to</span>
              <input type="time" className="exec-mini-select" defaultValue={guard.quietHours?.end || ''}
                onBlur={(e) => updateGuardrails(ownerUid, { quietHours: { start: guard.quietHours?.start || '', end: e.target.value, tz: guard.quietHours?.tz } }).catch((err) => onToast(err.message, true))} />
            </div>
          </div>
          <div style={{ paddingTop: 10, borderTop: '1px solid var(--pm-border-subtle)' }}>
            <div className="exec-field-label" style={{ marginBottom: 6 }}>Never auto-send from</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {stages.filter((s) => !s.archived).map((s) => {
                const on = guard.neverAutosendStages.includes(s.id);
                return (
                  <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, cursor: 'pointer' }}>
                    <input type="checkbox" checked={on} onChange={() => {
                      const next = on ? guard.neverAutosendStages.filter((x) => x !== s.id) : [...guard.neverAutosendStages, s.id];
                      updateGuardrails(ownerUid, { neverAutosendStages: next }).catch((err) => onToast(err.message, true));
                    }} />
                    {s.label}
                  </label>
                );
              })}
            </div>
          </div>
          <div className="exec-field" style={{ alignItems: 'flex-start' }}>
            <div><div className="exec-field-label">Allow list</div><div className="exec-field-help">Only auto-send to these (comma-separated). Empty = any.</div></div>
            <div className="exec-field-control">
              <input className="exec-link-input" style={{ width: 240 }} value={allowList} onChange={(e) => setAllowList(e.target.value)}
                onBlur={() => updateGuardrails(ownerUid, { senderAllowList: allowList.split(',').map((s) => s.trim()).filter(Boolean) }).catch((err) => onToast(err.message, true))} />
            </div>
          </div>
          <div className="exec-field" style={{ alignItems: 'flex-start' }}>
            <div><div className="exec-field-label">Deny list</div><div className="exec-field-help">Never auto-send to these (comma-separated).</div></div>
            <div className="exec-field-control">
              <input className="exec-link-input" style={{ width: 240 }} value={denyList} onChange={(e) => setDenyList(e.target.value)}
                onBlur={() => updateGuardrails(ownerUid, { senderDenyList: denyList.split(',').map((s) => s.trim()).filter(Boolean) }).catch((err) => onToast(err.message, true))} />
            </div>
          </div>
        </section>

        {/* Agent API key */}
        <section className="exec-card">
          <h3><KeyRound size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />Agent API</h3>
          <p className="sub">The local Mac-mini agent authenticates with this Bearer key. Endpoint: <code style={{ fontFamily: 'var(--pm-font-mono)' }}>{AGENT_ENDPOINT}</code> · mailbox: <code style={{ fontFamily: 'var(--pm-font-mono)' }}>{ownerEmail}</code></p>
          <div className="exec-key-row">
            <code className="exec-key-code">{displayKey || 'No key generated yet'}</code>
            {cfg.agentApiKey && !generatedKey && (
              <button className="exec-ghost-btn" onClick={() => setRevealKey((v) => !v)}>{revealKey ? 'Hide' : 'Reveal'}</button>
            )}
            {(displayKey && (generatedKey || revealKey)) && (
              <button className="exec-ghost-btn" onClick={() => { navigator.clipboard?.writeText(displayKey); onToast('Key copied'); }}><Copy size={13} /></button>
            )}
            <button className="exec-ghost-btn" onClick={rotateKey}><RefreshCw size={13} /> {cfg.agentApiKey ? 'Rotate' : 'Generate'}</button>
          </div>
          <div style={{ marginTop: 10 }}>
            <button
              type="button"
              className="exec-ghost-btn"
              onClick={() => {
                downloadExecInboxAgentGuide({ ownerUid, ownerEmail, ownerName, cfg, stages });
                onToast('Agent guide downloaded');
              }}
            >
              <Download size={13} /> Download agent guide
            </button>
          </div>
        </section>

        {/* Stage editor */}
        <section className="exec-card">
          <h3>Funnel stages</h3>
          <p className="sub">Rename, reorder, add or archive stages. IDs are stable so history and policies never break.</p>
          <div className="exec-stage-editor">
            {stages.map((s, idx) => (
              <div className="exec-stage-edit-row" key={s.id} style={{ opacity: s.archived ? 0.5 : 1 }}>
                <span className="exec-stage-handle">
                  <button onClick={() => moveStage(idx, -1)} aria-label="Up"><ChevronUp size={12} /></button>
                  <button onClick={() => moveStage(idx, 1)} aria-label="Down"><ChevronDown size={12} /></button>
                </span>
                <input value={s.label} placeholder="Display name" onChange={(e) => updateStage(idx, { label: e.target.value })} />
                <input value={s.gmailLabelName} placeholder="Gmail label" onChange={(e) => updateStage(idx, { gmailLabelName: e.target.value })} />
                <select value={s.agentPolicy} onChange={(e) => updateStage(idx, { agentPolicy: e.target.value as ExecInboxStage['agentPolicy'] })}>
                  {POLICIES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--pm-text-muted)' }} title="Agent may escalate into this stage but never auto-respond from it">
                  <input type="checkbox" checked={!!s.escalateOnly} onChange={(e) => updateStage(idx, { escalateOnly: e.target.checked })} /> esc
                </label>
                <button className="exec-icon-x" onClick={() => archiveStage(idx)} title={s.archived ? 'Restore' : 'Archive'}>
                  {s.archived ? <Plus size={14} /> : <Trash2 size={14} />}
                </button>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="exec-ghost-btn" onClick={addStage}><Plus size={13} /> Add stage</button>
            <button className="exec-send-btn" style={{ marginLeft: 'auto' }} onClick={commitStages} disabled={!stagesDirty}>Save funnel</button>
          </div>
        </section>

        {/* Agent activity */}
        <section className="exec-card">
          <h3><Bot size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />Agent activity</h3>
          <p className="sub">Audit trail of recent agent actions on this mailbox.</p>
          <div className="exec-log-list">
            {log.length === 0 && <span className="exec-field-help">No agent activity yet.</span>}
            {log.map((e) => (
              <div className="exec-log-row" key={e.id}>
                <span className="exec-log-action">{e.action}</span>
                <span className={`exec-log-result ${e.result}`}>{e.result}</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {e.fromStage ? `${e.fromStage} → ${e.toStage || e.fromStage}` : ''}{e.denyReason ? ` · ${e.denyReason}` : ''}{e.rationale ? ` · ${e.rationale}` : ''}
                </span>
                <span className="exec-log-time">{e.createdAt instanceof Date ? e.createdAt.toLocaleString() : ''}</span>
              </div>
            ))}
          </div>
        </section>

      </div>
    </div>
  );
};

export default ExecSuiteSettings;
